import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { companyLinks, productGroups } from '../data/navigation';
import { easeOutStrong } from '../utils/motion';
type Dropdown = 'products' | 'company' | null;
const dropdownMotion = {
  initial: {
    opacity: 0,
    y: -6
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -6
  },
  transition: {
    duration: 0.18,
    ease: easeOutStrong
  }
};
const topLinkClass = 'text-sm font-medium text-ink-muted hover:text-navy transition-colors duration-150';
const mobileLinkClass = 'block px-3 py-2 text-base font-medium text-ink-dark hover:bg-surface-muted rounded-md';
export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<Dropdown>(null);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const closeMobile = () => setMobileMenuOpen(false);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-[padding,background-color,box-shadow] duration-200 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-line py-3' : 'bg-white border-b border-line py-4'}`} data-id="element-862" data-mp="mgnm2lxellz8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-863" data-mp="webg19n6qpgc">
        <div className="flex justify-between items-center" data-id="element-864" data-mp="10f3ow1s5asc3">
          <a href="/" className="flex items-center" aria-label="SyNeural home" data-id="element-865" data-mp="156vh0gqy964c">
            <img src="https://cdn.magicpatterns.com/uploads/vBy52qowPNFNY53vkUADE7/SyNeural-logo-no-tagline-half-size.svg" alt="SyNeural" className={`w-auto transition-[height] duration-200 ease-out ${isScrolled ? 'h-8' : 'h-10'}`} data-id="element-866" data-mp="158ygdrdzsblq" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main" data-id="element-867" data-mp="ufqtzoyttoq3">
            <ul className="flex items-center gap-7" data-id="element-868" data-mp="udnzarmqb7f1">
              {/* Products */}
              <li className="relative" onMouseEnter={() => setOpenDropdown('products')} onMouseLeave={() => setOpenDropdown(null)} data-id="element-869" data-mp="pet4ssfefuf3">
                <button type="button" onClick={() => setOpenDropdown(openDropdown === 'products' ? null : 'products')} className={`${topLinkClass} flex items-center gap-1 py-2`} aria-expanded={openDropdown === 'products'} data-id="element-870" data-mp="zd0gowutzma8">
                  Products
                  <ChevronDown size={14} aria-hidden="true" className={`transition-transform duration-150 ${openDropdown === 'products' ? 'rotate-180' : ''}`} data-id="element-871" data-mp="1i51pos1bksxd" />
                </button>
                <AnimatePresence data-id="element-872" data-mp="3jz11deictcal">
                  {openDropdown === 'products' && <motion.div {...dropdownMotion} className="absolute top-full left-0 pt-2 w-[26rem]" data-id="element-873" data-mp="1zfp1p1x2q7js">
                      <div className="bg-white rounded-lg shadow-lg border border-line py-2" data-id="element-874" data-mp="1po2hwh6o705f">
                        {productGroups.map((group, groupIndex) => <div key={group.heading} className={groupIndex > 0 ? 'border-t border-line mt-2 pt-2' : ''} data-id="element-875" data-mp="1gontdna2cznk" data-mpi={groupIndex}>
                            <p className="px-4 pt-2 pb-1 text-xs font-semibold text-teal" data-id="element-876" data-mp="1kq9dntoxgg7s">{group.heading}</p>
                            {group.items.map(item => <div key={item.href} data-id="element-877" data-mp="1bpbateq86eqs">
                                <a href={item.href} onClick={() => setOpenDropdown(null)} className="block px-4 py-2 text-sm text-ink-dark hover:bg-surface-muted transition-colors duration-150" data-id="element-878" data-mp="1bq4bbxylc80h">
                                  <span className="font-semibold" data-id="element-879" data-mp="15yqhpyt30ouv">{item.label}</span>
                                  {item.badge && <span className="text-ink-muted font-normal" data-id="element-880" data-mp="wzmxgri6i3es"> · {item.badge}</span>}
                                  <span className="block text-xs text-ink-muted mt-0.5" data-id="element-881" data-mp="s0tlx4mgcmh9">{item.description}</span>
                                </a>
                                {item.children && <div className="mb-1" data-id="element-882" data-mp="ia789fyaq617">
                                    {item.children.map(child => <a key={child.href} href={child.href} onClick={() => setOpenDropdown(null)} className="flex gap-2 pl-6 pr-4 py-1.5 text-sm hover:bg-surface-muted transition-colors duration-150" data-id="element-883" data-mp="9aty0l2cldff">
                                        <span aria-hidden="true" className="text-ink-muted" data-id="element-884" data-mp="2fyud6guhaa1d">↳</span>
                                        <span data-id="element-885" data-mp="2kxnoq3q7fqyw">
                                          <span className="font-medium text-ink-dark" data-id="element-886" data-mp="2kyp42ke96zmf">{child.label}</span>
                                          <span className="block text-xs text-ink-muted" data-id="element-887" data-mp="2oyzficktei8o">{child.description}</span>
                                        </span>
                                      </a>)}
                                  </div>}
                              </div>)}
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>

              <li data-id="element-888" data-mp="1djk96i6pue0w">
                <a href="/solutions" className={topLinkClass} data-id="element-889" data-mp="1dj2g1ufcofx7">
                  Solutions
                </a>
              </li>

              {/* Company */}
              <li className="relative" onMouseEnter={() => setOpenDropdown('company')} onMouseLeave={() => setOpenDropdown(null)} data-id="element-890" data-mp="2bwy8jo3b0f7v">
                <button type="button" onClick={() => setOpenDropdown(openDropdown === 'company' ? null : 'company')} className={`${topLinkClass} flex items-center gap-1 py-2`} aria-expanded={openDropdown === 'company'} data-id="element-891" data-mp="2lmhp2n3jnwzk">
                  Company
                  <ChevronDown size={14} aria-hidden="true" className={`transition-transform duration-150 ${openDropdown === 'company' ? 'rotate-180' : ''}`} data-id="element-892" data-mp="26y4t3q4gie52" />
                </button>
                <AnimatePresence data-id="element-893" data-mp="3eb6tow3t6kg2">
                  {openDropdown === 'company' && <motion.div {...dropdownMotion} className="absolute top-full left-0 pt-2 w-72" data-id="element-894" data-mp="3lufxm9tjpqxq">
                      <div className="bg-white rounded-lg shadow-lg border border-line py-2" data-id="element-895" data-mp="3qt99jxt6p9vs">
                        {companyLinks.map(link => <a key={link.href} href={link.href} onClick={() => setOpenDropdown(null)} className="block px-4 py-2 text-sm text-ink-dark hover:bg-surface-muted transition-colors duration-150" data-id="element-896" data-mp="3bxta1d9u4ls0">
                            <span className="block font-semibold" data-id="element-897" data-mp="3uv4cf6qrjvbo">{link.label}</span>
                            <span className="block text-xs text-ink-muted" data-id="element-898" data-mp="3ut9xqa634zg1">{link.description}</span>
                          </a>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>

              <li data-id="element-899" data-mp="w773quobloc1">
                <a href="/contact" className={topLinkClass} data-id="element-900" data-mp="2s88tgt0x0n1i">
                  Contact
                </a>
              </li>
            </ul>
            <div className="w-px h-5 bg-line" aria-hidden="true" data-id="element-901" data-mp="2nghd1bbh3xp3"></div>
            <a href="/contact" className="bg-navy hover:bg-navy-light text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors duration-150 shadow-sm whitespace-nowrap" data-id="element-902" data-mp="2jfp28dmydkqp">
              Request a Demo
            </a>
          </nav>

          <button type="button" className="lg:hidden text-ink-dark p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" aria-expanded={mobileMenuOpen} data-id="element-903" data-mp="375jeou3d17af">
            {mobileMenuOpen ? <X size={24} data-id="element-904" data-mp="2x73octc5q21z" /> : <Menu size={24} data-id="element-905" data-mp="2x9pssk3r41fz" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Drawer */}
      <AnimatePresence data-id="element-906" data-mp="1qox2552roby3">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.22,
        ease: easeOutStrong
      }} className="lg:hidden bg-white border-b border-line overflow-hidden" data-id="element-907" data-mp="1lrf1s32tf51p">
            <nav className="px-4 pt-2 pb-6 space-y-1 max-h-[80vh] overflow-y-auto" aria-label="Mobile" data-id="element-908" data-mp="1lkcoc5efcc77">
              <div className="border-b border-line pb-2 mb-2" data-id="element-909" data-mp="1qf8q3x9gqdxi">
                <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider px-3 py-2" data-id="element-910" data-mp="1udy51ua5g3j9">Products</p>
                {productGroups.map(group => <div key={group.heading} className="mb-1" data-id="element-911" data-mp="24cdvrw59las8">
                    <p className="px-3 pt-2 pb-1 text-xs font-semibold text-teal" data-id="element-912" data-mp="24auec27vgtov">{group.heading}</p>
                    {group.items.map(item => <div key={item.href} data-id="element-913" data-mp="9zo3tjygibnh">
                        <a href={item.href} className={mobileLinkClass} onClick={closeMobile} data-id="element-914" data-mp="a0z5ne8cd9by">
                          {item.label}
                          {item.badge && <span className="text-sm font-normal text-ink-muted" data-id="element-915" data-mp="2txo6rlvbx2ny"> · {item.badge}</span>}
                        </a>
                        {item.children && <div data-id="element-916" data-mp="32xkrxdiifje9">
                            {item.children.map(child => <a key={child.href} href={child.href} className="block pl-6 pr-3 py-2 text-base text-ink-dark hover:bg-surface-muted rounded-md" onClick={closeMobile} data-id="element-917" data-mp="37wnw6vqo1ll6">
                                <span aria-hidden="true" className="text-ink-muted mr-2" data-id="element-918" data-mp="3mmmt6v6hiqz2">↳</span>
                                {child.label}
                              </a>)}
                          </div>}
                      </div>)}
                  </div>)}
              </div>

              <a href="/solutions" className={`${mobileLinkClass} py-3`} onClick={closeMobile} data-id="element-919" data-mp="ekf0fopph2sg">
                Solutions
              </a>

              <div className="border-y border-line py-2 my-2" data-id="element-920" data-mp="9j7ttszau9nd">
                <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider px-3 py-2" data-id="element-921" data-mp="4l6dhft9cofl">Company</p>
                {companyLinks.map(link => <a key={link.href} href={link.href} className={mobileLinkClass} onClick={closeMobile} data-id="element-922" data-mp="mh03n87z6vy">
                    {link.label}
                  </a>)}
              </div>

              <a href="/contact" className={`${mobileLinkClass} py-3`} onClick={closeMobile} data-id="element-923" data-mp="sdmstilm22b7">
                Contact
              </a>
              <div className="pt-4 px-3" data-id="element-924" data-mp="jji8li0zfp4n">
                <a href="/contact" onClick={closeMobile} className="block w-full text-center bg-navy text-white font-medium px-5 py-3 rounded-md shadow-sm" data-id="element-925" data-mp="jiqa9zg03dao">
                  Request a Demo
                </a>
              </div>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}